import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';

const EmailDiscountSignup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError('Email address cannot be empty');
      inputRef.current?.focus();
      setIsLoading(false);
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address');
      inputRef.current?.focus();
      setIsLoading(false);
      return;
    }

    const templateParams = {
      to_email: trimmedEmail,
      email: trimmedEmail,
      recipient_email: trimmedEmail,
      user_email: trimmedEmail,
      from_name: 'Asclepius Shilajit',
      reply_to: trimmedEmail,
    };

    try {
      console.log('Sending email with params:', templateParams);

      const response = await emailjs.send(
        'service_awr5l0g',
        'template_z173gm4',
        templateParams,
        'gDbAQE8Cm3tzMASFE'
      );

      if (response.status === 200) {
        setIsSubmitted(true);
        setEmail('');
      } else {
        throw new Error(`Email send failed with status: ${response.status}`);
      }
    } catch (err: any) {
      console.error('EmailJS Error Details:', err);
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setIsSubmitted(false);
    setEmail('');
    setError('');
    inputRef.current?.focus();
  };

  return (
    <section className="py-20 bg-gradient-to-br from-[#f0f4f8] to-[#e6eef3]">
      <div className="px-4 w-full">
        {isSubmitted ? (
          <div className="text-center space-y-6 max-w-3xl mx-auto">
            <div className="bg-green-100 border-2 border-green-300 text-green-700 p-8 rounded-2xl shadow-md">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-20 w-20 mx-auto mb-6 text-green-500" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
              <h2 className="text-3xl font-bold mb-4">Thank You!</h2>
              <p className="text-green-600 mb-6 text-lg">
                You've been subscribed to our exclusive updates.
              </p>
              <button 
                onClick={handleReset}
                className="px-8 py-3 bg-[#2b603e] text-white text-lg rounded-xl hover:bg-[#1a3d25] transition-colors duration-300 shadow-lg hover:shadow-xl"
              >
                Subscribe Another Email
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8 max-w-3xl mx-auto">
            <div className="text-center">
              <h2 className="text-4xl font-extrabold text-[#2b603e] mb-4">
                Get 17% off your first purchase
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Enjoy an immediate discount right after signing up for our occasional newsletter. We never spam.
              </p>
            </div>

            <div className="relative">
              <input
                ref={inputRef}
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full px-6 py-5 pl-12 border-2 border-[#2b603e]/20 rounded-2xl 
                  focus:outline-none focus:ring-3 focus:ring-[#2b603e]/50 focus:border-transparent 
                  transition-all duration-300 text-gray-800 text-lg placeholder-gray-500
                  bg-gray-50/50"
                required
              />
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-7 w-7 text-[#2b603e]/60" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
                  />
                </svg>
              </div>
            </div>

            {error && (
              <div className="max-w-3xl mx-auto">
                <div className="bg-red-100 border-2 border-red-300 text-red-700 px-6 py-4 rounded-xl">
                  <p className="text-base">{error}</p>
                </div>
              </div>
            )}

            <div className="text-center">
              <button
                type="submit"
                disabled={isLoading}
                className={`px-10 py-5 text-lg rounded-2xl text-white font-bold uppercase tracking-wider 
                  transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-3 
                  max-w-md w-full mx-auto shadow-lg hover:shadow-xl
                  ${isLoading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-[#2b603e] hover:bg-[#1a3d25] active:scale-95'
                  }`}
              >
                {isLoading ? 'Subscribing...' : 'Subscribe Now'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default EmailDiscountSignup;
